<template>
    <v-card>
        <v-card-title>
            <v-row no-gutters>
                Experiment {{ title }} Configurations
                <v-spacer/>
                <v-btn color="primary" @click="addNewRelationshipConfig">Add {{ title }} Config</v-btn>
            </v-row>
        </v-card-title>
        <v-card-text>
            <v-data-table :headers="headers"
                          :items="experimentRelationshipConfigs">
                <template v-slot:item.registrySchema="{ item }">
                    {{ item.registrySchema.displayName }}
                </template>
                <template v-slot:item.defaultRegistryEntries="{ item }">
                    {{ getDefaultEntries(item) }}
                </template>
                <template v-slot:item.isSchemaConfigLevelRelationshipConfig="{ item }">
                    <v-icon>{{ isInheritedRelationshipConfig(item) ? "$check" : "" }}</v-icon>
                </template>
                <template v-slot:item.actions="{ item }">
                    <v-icon-btn v-if="showActionButtons(item)" @click="showEditRelationshipConfigForm(item)"><v-icon>$pencil</v-icon></v-icon-btn>
                    <v-icon-btn v-if="showActionButtons(item)" @click="deleteRelationshipConfig(item)"><v-icon>$delete</v-icon></v-icon-btn>
                </template>
            </v-data-table>
        </v-card-text>
        <v-dialog v-model="editRelationshipConfigDialogVisible" width="75%">
            <relationship-config-form
                :all-registry-schemas="targetRegistrySchemas"
                :type="type"
                :experiment-relationship-config-to-edit="selectedRelationshipConfig"
                @save-experiment-relationship-config="handleSaveRelationshipConfig"
                @cancel-edit-relationship-config="closeEditRelationshipConfigDialog"
            />
        </v-dialog>
    </v-card>
</template>
<script setup lang="ts">

import { computed, ref } from "vue";
import { registriesAPI, SavedRegistrySchema } from "@/services/registries/registriesAPI";
import { withLoadingScreen } from "@/lib/loading";
import RelationshipConfigForm from "@/components/ExperimentRelationshipConfigs/RelationshipConfigForm.vue";
import {
    ExperimentSetupRelationshipConfig,
    ManageExperimentSetupRelationshipConfigDTO,
    SavedExperimentSetupRelationshipConfig,
} from "@/services/experimentRelationships/models";
import _ from "lodash";
import {
    baseRelationshipConfigAPI,
} from "@/services/experimentRelationships/experimentRelationshipConfigsAPI";

const { experimentRelationshipConfigs, type, showInheritanceFromSchemaConfig } = defineProps<{
    experimentRelationshipConfigs: ManageExperimentSetupRelationshipConfigDTO[];
    type: string;
    showInheritanceFromSchemaConfig?: boolean;
}>();

function isInheritedRelationshipConfig(config: ManageExperimentSetupRelationshipConfigDTO) {
    return !_.isNil(config.schemaConfigId) && _.isNil(config.experimentGroupId);
}

function showActionButtons(item: ManageExperimentSetupRelationshipConfigDTO) {
    if (showInheritanceFromSchemaConfig) {
        return !isInheritedRelationshipConfig(item);
    }
    return true;
}

const emit = defineEmits<{
    (e: "saveExperimentRelationshipConfig", experimentRelationshipConfig: ExperimentSetupRelationshipConfig | SavedExperimentSetupRelationshipConfig): void;
    (e: "deletedExperimentRelationshipConfig", experimentRelationshipConfig: SavedExperimentSetupRelationshipConfig): void;
}>();

const targetRegistrySchemas = ref<SavedRegistrySchema[]>([]);
const editRelationshipConfigDialogVisible = ref<boolean>(false);
const selectedRelationshipConfig = ref<SavedExperimentSetupRelationshipConfig>();

const headers = computed(() => {
    const tableHeaders = [
        {
            title: "Registry",
            value: "registrySchema",
        },
        {
            title: "Required?",
            value: "required"
        },
        {
            title: "Allows multiple?",
            value: "allowMultiple"
        },
        {
            title: "Default value(s)",
            value: "defaultRegistryEntries"
        },
    ];

    if (showInheritanceFromSchemaConfig) {
        tableHeaders.push({
            title: `Schema ${title.value}`,
            value: "isSchemaConfigLevelRelationshipConfig",
        });
    }

    tableHeaders.push({
        title: "Actions",
        value: "actions",
    });

    return tableHeaders;
});

const title = computed(() => {
    return _.capitalize(type);
});

function getDefaultEntries(config: ManageExperimentSetupRelationshipConfigDTO) {
    const defaultRegistryEntries = config.defaultRegistryEntries;
    if (defaultRegistryEntries) {
        return defaultRegistryEntries.map((registryEntry) => {
            return `${registryEntry.id} - ${registryEntry.displayName}`;
        }).join(", ");
    } else {
        return "";
    }
}

function addNewRelationshipConfig() {
    selectedRelationshipConfig.value = undefined;
    editRelationshipConfigDialogVisible.value = true;
}

function showEditRelationshipConfigForm(config: SavedExperimentSetupRelationshipConfig) {
    selectedRelationshipConfig.value = config;
    editRelationshipConfigDialogVisible.value = true;
}

function handleSaveRelationshipConfig(config: ExperimentSetupRelationshipConfig | SavedExperimentSetupRelationshipConfig) {
    emit("saveExperimentRelationshipConfig", {
        ...config,
        type: type
    });
    closeEditRelationshipConfigDialog();
}

function closeEditRelationshipConfigDialog() {
    editRelationshipConfigDialogVisible.value = false;
    selectedRelationshipConfig.value = undefined;
}

async function deleteRelationshipConfig(config: ManageExperimentSetupRelationshipConfigDTO) {
    if (confirm(`Are you sure you want to delete ${config.registrySchema.displayName} ${config.type}?`)) {
        await baseRelationshipConfigAPI.deleteRelationshipConfig(config.id);
        emit("deletedExperimentRelationshipConfig", config);
    }
}

async function fetchRegistrySchemas() {
    await withLoadingScreen(async () => {
        targetRegistrySchemas.value = await registriesAPI.fetchAllRegistrySchemasForRelationshipType(type);
    });
}

fetchRegistrySchemas();
</script>
